import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AppLayout } from 'src/components/layout/app-layout';
import { useSetPageTitle } from 'src/hooks/useSetPageTitle';
import { useIsMobile } from 'src/hooks/useMobile';
import { useGetSurveyGroupPreviewQuery, useGetSurveyGroupResultsQuery } from 'src/redux/survey/survey-groups.endpoints';
import { SurveySummaryData } from 'src/redux/survey/survey.types';
import { SurveyQuestionType } from 'src/redux/survey/survey.types';
import { SidebarProvider, SidebarTrigger } from 'src/components/ui/sidebar';
import { SurveyGroupSurveyFilterBar } from './components/survey-group-survey-filter-bar';
import { aggregateGroupPayloadTotalUsers } from '../view-survey/view-survey.helpers';
import { combinedQuestionsFromGroupResultsPayload } from '../view-survey/survey-group-results-merge.helpers';
import { SurveyGroupResultsSidebar } from './survey-group-results-sidebar';
import { useSurveyGroupResultsTabs } from './survey-group-results-nav';
import { buildSurveyGroupCompareQuestionBlocks } from './survey-group-compare.helpers';
import {
  SurveyGroupCompareAnswerAxisTick,
  SurveyGroupCompareTooltip,
} from './components/survey-group-compare-answer-axis-tick';
import SurveyGroupCompareLoadingBar from './components/survey-group-compare-loading-bar';
import { useSurveyGroupCompareLoadingProgress } from './use-survey-group-compare-loading-progress';

const SurveyGroupComparePage = (): ReactElement => {
  useSetPageTitle('Compare survey group results');
  const { clientId, surveyGroupId } = useParams<{ clientId?: string; surveyGroupId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useIsMobile();
  const { tabs, activeTab } = useSurveyGroupResultsTabs(surveyGroupId, clientId);

  const { data: groupResults, currentData, isFetching, isError } = useGetSurveyGroupResultsQuery(
    {
      groupId: surveyGroupId ?? '',
      include: 'stats,summary,trends,trends_events',
    },
    { skip: !surveyGroupId }
  );

  const resolvedGroupResults = currentData ?? (!isFetching ? groupResults : undefined);

  const {
    data: groupPreview,
    isFetching: isPreviewFetching,
    isLoading: isPreviewLoading,
  } = useGetSurveyGroupPreviewQuery(surveyGroupId ?? '', {
    skip: !surveyGroupId,
  });

  const [selectedGroupSurveyIds, setSelectedGroupSurveyIds] = useState<string[]>(() => {
    const p = searchParams.get('groupSurveys');
    return p ? p.split(',').filter(Boolean) : [];
  });

  useEffect(() => {
    if (!resolvedGroupResults?.surveys?.length) {
      return;
    }
    setSelectedGroupSurveyIds((prev) => {
      if (prev.length > 0) {
        return prev;
      }
      return resolvedGroupResults.surveys.map((s) => s.ext_survey_id);
    });
  }, [resolvedGroupResults]);

  const updateGroupSurveySelection = useCallback(
    (ids: string[]) => {
      setSelectedGroupSurveyIds(ids);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (ids.length) {
            next.set('groupSurveys', ids.join(','));
          } else {
            next.delete('groupSurveys');
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const visibleGroupSurveys = useMemo(() => {
    if (!resolvedGroupResults?.surveys?.length) {
      return [];
    }
    const selected = new Set(selectedGroupSurveyIds);
    return resolvedGroupResults.surveys.filter((s) => selected.has(s.ext_survey_id));
  }, [resolvedGroupResults, selectedGroupSurveyIds]);

  const showSummaries = useMemo(() => {
    return visibleGroupSurveys.some((p) => {
      const s = p.summary as SurveySummaryData | undefined;
      const q = s?.questions;
      if (!q) {
        return false;
      }
      return Array.isArray(q) ? q.length > 0 : Object.keys(q as object).length > 0;
    });
  }, [visibleGroupSurveys]);

  const mergedQuestions = useMemo(() => {
    if (!resolvedGroupResults) {
      return [];
    }
    return combinedQuestionsFromGroupResultsPayload(resolvedGroupResults, showSummaries, groupPreview);
  }, [resolvedGroupResults, showSummaries, groupPreview]);

  const compareBlocks = useMemo(
    () => buildSurveyGroupCompareQuestionBlocks(visibleGroupSurveys, groupPreview),
    [visibleGroupSurveys, groupPreview]
  );

  const aggregatedTotalUsers = useMemo(
    () => aggregateGroupPayloadTotalUsers(visibleGroupSurveys),
    [visibleGroupSurveys]
  );

  const isCompareDataLoading = Boolean(
    surveyGroupId &&
    !isError &&
    (
      (isFetching && resolvedGroupResults === undefined) ||
      (Boolean(resolvedGroupResults) && visibleGroupSurveys.length > 0 && (isPreviewFetching || isPreviewLoading))
    )
  );

  const { visible: compareLoadingBarVisible, percent: compareLoadingPercent } =
    useSurveyGroupCompareLoadingProgress(isCompareDataLoading);

  return (
    <AppLayout
      activePage="/results"
      navbarProps={{
        showBackButton: false,
        backPath: clientId ? `/client/${clientId}/reports` : '/surveys',
        tabs,
        activeTab,
        onTabChange: () => undefined,
      }}
    >
      <div className="flex h-[calc(100vh-8rem)] min-h-0 w-full flex-col">
        <SidebarProvider className="min-h-0 flex-1 flex-row">
          <div className="min-w-0 flex-1 p-4 md:p-1">
            {isMobile && (
              <div className="flex size-12 items-center justify-center">
                <SidebarTrigger />
              </div>
            )}
            {!surveyGroupId ? (
              <p className="text-sm text-gray-600">
                Missing survey group.
              </p>
            ) : null}
            {compareLoadingBarVisible || isCompareDataLoading ? (
              <SurveyGroupCompareLoadingBar
                percent={compareLoadingPercent}
                visible={compareLoadingBarVisible || isCompareDataLoading}
              />
            ) : null}
            {isError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-8 text-sm text-red-800">
                Could not load survey group results. Check that you have access to this group and try again.
              </div>
            ) : null}
            {resolvedGroupResults?.surveys?.length ? (
              <>
                <SurveyGroupSurveyFilterBar
                  surveys={resolvedGroupResults.surveys}
                  selectedExtSurveyIds={selectedGroupSurveyIds}
                  onChange={updateGroupSurveySelection}
                />
                {visibleGroupSurveys.length === 0 ? (
                  <p className="text-sm text-gray-600">
                    Select at least one survey above to compare.
                  </p>
                ) : !groupPreview?.questions?.length && !isCompareDataLoading ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-sm text-amber-900">
                    Compare needs the survey group question map from the server. Try again after the group preview loads, or contact support if this persists.
                  </div>
                ) : !isCompareDataLoading && groupPreview?.questions?.length ? (
                  <div className="flex flex-col gap-10 mt-4 w-full max-w-5xl">
                    <p className="text-sm text-gray-600">
                      Percentages show each answer’s share of respondents for that survey (same logic as the main results view). Surveys you turn off in the filter above are excluded from the chart.
                    </p>
                    {compareBlocks.map((block, idx) => (
                      <section
                        key={block.questionClusterId}
                        id={`compare-q-${block.questionClusterId}`}
                        className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm md:p-6"
                      >
                        <div className="flex items-start gap-3 mb-4">
                          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-semibold text-violet-900">
                            {idx + 1}
                          </span>
                          <div>
                            <h2 className="text-lg font-semibold text-gray-900">
                              {block.title}
                            </h2>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {block.questionType === SurveyQuestionType.MultiSelect ? 'Multi-select' : block.questionType === SurveyQuestionType.Freeform ? 'Freeform' : 'Single-select'}
                            </p>
                          </div>
                        </div>
                        {block.questionType === SurveyQuestionType.Freeform ? (
                          <p className="text-sm text-gray-600">
                            Freeform questions are not included in compare charts. View responses on the Overview tab.
                          </p>
                        ) : block.chartRows.length === 0 ? (
                          <p className="text-sm text-gray-600">
                            No answer breakdown available for this question.
                          </p>
                        ) : (
                          <div className="h-[min(420px,70vh)] w-full min-h-[280px]">
                            <ResponsiveContainer
                              width="100%"
                              height="100%"
                            >
                              <BarChart
                                data={block.chartRows}
                                margin={{
                                  top: 8,
                                  right: 12,
                                  left: 4,
                                  bottom: block.hasAnswerImages ? 108 : 64,
                                }}
                              >
                                <CartesianGrid
                                  strokeDasharray="3 3"
                                  stroke="#e5e7eb"
                                />
                                <XAxis
                                  dataKey="answerLabel"
                                  interval={0}
                                  tick={
                                    block.hasAnswerImages
                                      ? (props) => (
                                        <SurveyGroupCompareAnswerAxisTick
                                          {...props}
                                          rows={block.chartRows}
                                        />
                                      )
                                      : { fontSize: 11, fill: '#6b7280' }
                                  }
                                  angle={block.hasAnswerImages ? 0 : -28}
                                  textAnchor={block.hasAnswerImages ? 'middle' : 'end'}
                                  height={block.hasAnswerImages ? 100 : 72}
                                />
                                <YAxis
                                  domain={[0, 100]}
                                  tick={{ fontSize: 11 }}
                                  tickFormatter={(v) => `${v}%`}
                                  width={44}
                                />
                                <Tooltip content={SurveyGroupCompareTooltip} />
                                <Legend wrapperStyle={{ paddingTop: 12 }} />
                                {block.series.map((s) => (
                                  <Bar
                                    key={s.dataKey}
                                    dataKey={s.dataKey}
                                    name={s.label}
                                    fill={s.color}
                                    radius={[4, 4, 0, 0]}
                                    maxBarSize={48}
                                  />
                                ))}
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                      </section>
                    ))}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
          {surveyGroupId && resolvedGroupResults ? (
            <SurveyGroupResultsSidebar
              surveyGroupId={surveyGroupId}
              surveyGroupTitle={groupPreview?.name?.trim() || 'Survey group'}
              groupResults={resolvedGroupResults}
              mergedQuestions={mergedQuestions}
              aggregatedTotalUsers={aggregatedTotalUsers}
              showSummaries={showSummaries}
            />
          ) : null}
        </SidebarProvider>
      </div>
    </AppLayout>
  );
};

export default SurveyGroupComparePage;
